import React, { type FC, memo, useCallback, useState } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import type { ExtraType, WheelType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import { useCarConfigSelector, useCarConfigDispatch } from "../../../../../redux/store";
import { setWheels } from "../../../../../redux/slices/CarConfigBasketSlice";
import SidePanel from "../../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import WheelDetails from "./WheelDetails";
import Price from "../../../common/price/Price";
import CarDataConsolidator from "../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import AddToBasket from "../../../common/AddToBasket";
import cardStyles from "../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import styles from "./Wheels.module.scss";
import {
    trackWheelAddToBasket,
    trackWheelsTyreMoreInfoClose,
    trackWheelsTyreMoreInfoOpen,
} from "../../../../../utils/tracking";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import useAphModal from "../../../../../hooks/useAphModal";
import { MODAL_OTHER_GRADE } from "../../../../../utils/modalConstants";
import type { WheelFromOtherGradesType } from "../../../../../../../shared-logic/types/AvailableInOtherGradesType";
import { AvailableInOtherGradeModalType } from "../../../../../utils/types/SectionTypes";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { CarConfigLabel, getCarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import { setActiveSpin, SpinnerType } from "../../../../../redux/slices/CarConfigSpinNavigationSlice";
import { addNotification } from "../../../../../../../../common-deprecated/redux/actions/NotificationActions";

const WheelItem: FC<{
    wheel: WheelType | ExtraType | WheelFromOtherGradesType;
    index: number;
    wheelOtherGrade?: boolean;
    onlyChild?: boolean;
}> = ({ wheel, index, wheelOtherGrade, onlyChild }) => {
    const dispatch = useCarConfigDispatch();

    const activeWheel = useCarConfigSelector((state) => state.basket.wheel);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const tyres = useCarConfigSelector(
        (state) =>
            "objectType" in wheel ? CarDataConsolidator.getWheelTyres(wheel as WheelType, state.items.tyres) : [],
        shallowEqual,
    );

    const sidePanelTitle = useCarConfigLabel("carConfigTyreInfo");

    const [showInfo, setShowInfo] = useState<boolean>(false);

    const { showModalHelper } = useAphModal();

    const showTyreInfo = !carConfigSettings.hideTyreInfo;
    const isActive = activeWheel.id === wheel.id;
    const hasTyres = tyres.length !== 0;
    const activeSpinner = useCarConfigSelector((state) => state.carConfigSpinNavigation.activeSpin);

    const clickHandler = useCallback(() => {
        if (activeSpinner !== SpinnerType.Car) {
            dispatch(setActiveSpin(SpinnerType.Car));
        }
        if (wheelOtherGrade && !("standard" in wheel)) {
            showModalHelper(MODAL_OTHER_GRADE, {
                availableInOtherGradesProps: {
                    section: AvailableInOtherGradeModalType.Wheel,
                    wheel,
                },
            });
        } else {
            trackWheelAddToBasket();
            dispatch(setWheels({ wheel: wheel as WheelType }));
            dispatch(
                addNotification([
                    {
                        title: getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigA11yWheelUpdated"),
                        description: getCarConfigLabel(
                            { commonSettings, carConfigSettings },
                            "carConfigA11yWheelUpdatedDescription",
                            { wheel: wheel.name },
                        ),
                        srOnly: true,
                    },
                ]),
            );
        }
    }, [dispatch, wheel, onlyChild, activeSpinner]);
    return (
        <>
            {/* Explainer: Article should have an onClick event, but also the button with the plus icon for a11y reasons.
                Add aria-pressed on the button as true and false depending on active state and update the article classes based on that as well */}
            <article
                className={clsx(
                    cardStyles.card,
                    !onlyChild && cardStyles.clickable,
                    styles.wheel,
                    isActive && cardStyles.active,
                )}
                onClick={
                    onlyChild
                        ? activeSpinner !== SpinnerType.Car
                            ? () => dispatch(setActiveSpin(SpinnerType.Car))
                            : undefined
                        : clickHandler
                }
                style={{ "--or-animation-delay": `${(index + 1) * 100 + 4}` }}
            >
                <figure className={styles["wheel-wrapper"]}>
                    <img
                        {...getCarDBAssetSrcSet(
                            commonSettings,
                            [
                                { width: 90, height: 90 },
                                { width: 125, height: 125 },
                                { width: 180, height: 180 },
                                { width: 250, height: 250 },
                            ],
                            wheel.image,
                        )}
                        width="90"
                        height="90"
                        sizes="(max-width: 575px) 90px, 125px"
                        alt=""
                        role="presentation"
                        loading="lazy"
                    />
                </figure>
                <div className={styles.bottom}>
                    <Text tag="h3" variant="apheleia-body" color="highlight-black">
                        <strong>{wheel.name}</strong>
                    </Text>
                    <div className={styles.info}>
                        <div>
                            {hasTyres && showTyreInfo && (
                                <Button
                                    variant="apheleia-text"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        trackWheelsTyreMoreInfoOpen();
                                        setShowInfo(true);
                                    }}
                                >
                                    <CarConfigLabel label="carConfigWheelMoreInfo" />
                                </Button>
                            )}
                        </div>
                        <div className={styles.price}>
                            <Text variant="apheleia-price">
                                {"standard" in wheel && !wheel.standard && (
                                    <Price
                                        price={wheel.price}
                                        forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                                    />
                                )}
                            </Text>
                            {onlyChild ? (
                                <div className={clsx(cardStyles.indicator, styles.button, styles["static-indicator"])}>
                                    <Icon variant="check" sizing="xs" aria-hidden="true" />
                                </div>
                            ) : (
                                <AddToBasket
                                    itemName={wheel.name}
                                    onClick={clickHandler}
                                    active={isActive}
                                    checkType
                                    className={clsx(cardStyles.indicator, styles.button)}
                                    aria-pressed={isActive}
                                    data-testid="wheel-add-to-basket-button"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </article>
            {hasTyres && showTyreInfo && (
                <SidePanel
                    open={showInfo}
                    title={sidePanelTitle}
                    onRequestClose={() => {
                        trackWheelsTyreMoreInfoClose();
                        setShowInfo(false);
                    }}
                >
                    <WheelDetails tyres={tyres} />
                </SidePanel>
            )}
        </>
    );
};

export default memo(WheelItem);
